import { Button, Grid } from "@mui/material";
import { useFormik } from "formik";
import CustomSelect from "../../../../../components/CustomSelect";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import Text from "../../../../../components/Text";
import ConfirmationModal from "../../../../../components/app/ConfirmationModal";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { getXtenantId, showToastOnApiCall } from "../../../../../utils/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateClaimStatus } from "../../../../../queries/billings/claims/use-update-claim-status";

import { CLAIMS_STATUS_LIST } from "./claims-constants";
import * as Yup from "yup";

interface ChangeClaimStatusProps {
  open: boolean;
  onClose: () => void;
  disclosureHost?: DisclosureHost;
}

const ChangeClaimStatus = ({
  open,
  onClose,
  disclosureHost,
}: ChangeClaimStatusProps) => {
  const client = useQueryClient();
  const { mutateAsync: updateClaimStatus } = useUpdateClaimStatus();
  const confirmDisclosure = useDisclosure();
  const showSnackbar = useSnackbar();
  const formik = useFormik<{ claimStatus: string }>({
    initialValues: {
      claimStatus: undefined as any,
    },
    validationSchema: Yup.object().shape({
      claimStatus: Yup.string().required("Status is required"),
    }),
    enableReinitialize: true,
    onSubmit: () => {
      confirmDisclosure.open();
    },
  });

  async function handleConfirmStatusChange() {
    const payload = {
      claimId: disclosureHost?.data?.uuid,
      status: formik.values?.claimStatus,
      xTENANTID: getXtenantId(),
    };
    confirmDisclosure.close();
    showToastAndResetForm(updateClaimStatus(payload));
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    showToastOnApiCall({
      apiCall,
      showSnackbar,
      onSave: () => {
        client.invalidateQueries({
          queryKey: ["get-claims-list-v2"],
        });
        client.invalidateQueries({
          queryKey: ["Get-claim-count"],
        });
        // Refresh the Ready for Billing list so the encounter's Billed Status
        // reflects the new claim status. Paper claims rely on this manual
        // status change (unlike electronic claims, which refresh on submit).
        client.invalidateQueries({
          queryKey: ["appointment-list-v2"],
        });
        closeAndResetForm();
      },
    });
  }

  function closeAndResetForm() {
    formik.resetForm();
    disclosureHost?.close();
    onClose();
  }

  const selectedStatusLabel =
    CLAIMS_STATUS_LIST.find((s) => s.value === formik.values?.claimStatus)
      ?.label ?? formik.values?.claimStatus;

  return (
    <>
      <SideSheetDrawer
        open={open}
        onClose={() => closeAndResetForm()}
        title="Update Status"
        w="600px"
        footerActions={
          <Button
            size="large"
            variant="contained"
            onClick={() => formik.handleSubmit()}
          >
            Send
          </Button>
        }
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <CustomSelect
              label="Status"
              required
              options={CLAIMS_STATUS_LIST}
              value={formik.values?.claimStatus}
              onChange={(value) => {
                formik.setFieldValue("claimStatus", value ?? undefined);
              }}
              error={formik.touched.claimStatus && formik.errors.claimStatus}
            />
          </Grid>
        </Grid>
      </SideSheetDrawer>

      <ConfirmationModal
        maxWidth="sm"
        disclosureHost={confirmDisclosure}
        onConfirm={handleConfirmStatusChange}
        confirmationText="Yes, Update"
        cancelText="Cancel"
        title={
          <Text fontWeight={"medium"} fontSize={"20px"}>
            Confirm Status Change
          </Text>
        }
        content={`Are you sure you want to change the claim status to "${selectedStatusLabel}" ?`}
      />
    </>
  );
};

export default ChangeClaimStatus;
